import React, {useEffect} from "react";
import {useHistory} from "react-router-dom";
import {useForm} from "antd/lib/form/Form";
import {useDispatch, useSelector} from "react-redux";
import Empty from "antd/lib/empty";

import {sensorsAC} from "../../store/branches/sensors/actionCreators";
import {useCurrentSelection} from "../../hooks/useCurrentSelection";
import EditSensorsSettingsForm from "./components/EditSensorsSettingsForm";
import DashboardAlert from "../Alerts/dachboard";
import {LoadingStatus} from "../../store/status";
import {selectSensorsState} from "../../store/selectors";
import Spinner from "../Spinner";

// Strips binary floating-point noise (e.g. 0.575 * 100 === 57.49999999999999)
// without arbitrarily truncating precision a user actually entered.
const cleanFloat = (value: number) => Number(value.toPrecision(12));

const EditSensorsSettings: React.FC = () => {
    const [form] = useForm();
    const history = useHistory();
    const dispatch = useDispatch();

    const {device} = useCurrentSelection();
    const {status, sensorSettings, units} = useSelector(selectSensorsState);
    
    const isCorrectDevice = ["0005", "0006"].includes(device?.device_type);

    const onSubmit = (values: any) => {
        // The settings endpoint replaces sensors_settings wholesale (it's not a merge),
        // so every sensor must round-trip even if its fields weren't rendered this
        // session — e.g. Input 9/10's inactive CLI/Pulse mode, which UnifiedInputForm
        // only mounts for whichever mode is currently selected. Seed with what's
        // already stored, then let the submitted form values override on top.
        const obj: any = {};
        sensorSettings?.forEach((group) => {
            group.sensors?.forEach((sensor) => {
                obj[sensor.id] = {
                    unit: sensor.unit,
                    type: sensor.type,
                    k: sensor.k_value,
                    c: sensor.c_value,
                    decimals: sensor.decimals,
                    raw1: sensor.raw1,
                    scaled1: sensor.scaled1,
                    raw2: sensor.raw2,
                    scaled2: sensor.scaled2,
                };
            });
        });

        const arrayFromObject = Object.entries(values);
        arrayFromObject.forEach((item: any) => {
            const valueId: string = item[0].split(" ")[0];
            const valueName: string = item[0].split(" ")[1];
            const newValue = item[1] === "Digital" ? "BOOL" : item[1] === "Analog" ? "NUMERIC" : item[1];

            if (!obj[valueId]) {
                obj[valueId] = {};
            }
            obj[valueId][valueName] = newValue;
        });

        Object.keys(obj).forEach((sensorId: string) => {
            const sensor = obj[sensorId];
            const {raw1, raw2, scaled1, scaled2} = sensor;
            const hasCompletePoints = [raw1, raw2, scaled1, scaled2].every((value) => value !== undefined);
            if (hasCompletePoints && raw2 !== raw1) {
                const k = cleanFloat((scaled2 - scaled1) / (raw2 - raw1));
                const c = cleanFloat(scaled1 - k * raw1);
                sensor.k = k;
                sensor.c = c;
            }

            // Input 9/10's mode select (UnifiedInputForm) isn't part of the persisted
            // sensor shape — it's carried on the CLI sensor's record and translated
            // into `type` here, since CLI's sensor id is the one that survives
            // regardless of which mode (CLI/Pulse/Contact) is actually selected.
            if (sensor.inputMode !== undefined) {
                const {inputMode} = sensor;
                delete sensor.inputMode;
                if (inputMode === "Contact") {
                    obj[sensorId] = {type: "CONTACT"};
                } else if (inputMode === "Pulse") {
                    // Scaling fields were rendered against the Pulse sensor's id this
                    // session, not this (CLI) sensor's — wholesale-clear its own
                    // legacy config so a later reload's mode-detection heuristic
                    // doesn't mistake stale CLI calibration for "CLI is active".
                    obj[sensorId] = {type: "NUMERIC"};
                } else {
                    sensor.type = "NUMERIC";
                }
            }
        });

        dispatch(sensorsAC.addSensorSettings({device_id: device.id, data: {"sensor_types": obj}}));
    };

    const onCancel = () => {
        form.resetFields();
        history.push("/dashboard");
    };

    useEffect(() => {
        if (!device) {
            dispatch(sensorsAC.setSensorsStatusOperation(LoadingStatus.FETCH_SENSORS_WITHOUT_DEVICE));
            return;
        }

        dispatch(sensorsAC.fetchSensorSettings(device.id));

        return () => {
            dispatch(sensorsAC.setSensorSettings(null));
        };
    }, [dispatch, device]);

    useEffect(() => {
        if (device && !isCorrectDevice) {
            history.push("/dashboard");
        }

    }, [device, history, isCorrectDevice]);

    if (status === LoadingStatus.LOADING) {
        return <Spinner/>;
    }

    if (sensorSettings) {
        return (
            <React.Fragment>
                <DashboardAlert/>

                <EditSensorsSettingsForm form={form}
                                         onSubmit={onSubmit}
                                         onCancel={onCancel}

                                         sensorSettings={sensorSettings}
                                         units={units}
                                         deviceTitle={device.title}
                                         deviceType={device?.device_type}
                />
            </React.Fragment>
        );
    }
    return <Empty description="Sensor names is empty!"/>;
};
export default EditSensorsSettings;
